"use client";

import Link from "next/link";
import { useRef } from "react";

/**
 * One of the format cards. The blue panel carries a soft light that tracks the
 * pointer, the same idea the registration panel uses, so the two dark surfaces
 * on the page react the same way.
 */
export function PillarCard({
  href,
  title,
  children,
}: {
  href: string;
  title: string;
  children: React.ReactNode;
}) {
  const panelRef = useRef<HTMLSpanElement>(null);
  const glowRef = useRef<HTMLSpanElement>(null);

  function handleMove(event: React.PointerEvent<HTMLAnchorElement>) {
    const panel = panelRef.current;
    const glow = glowRef.current;
    if (!panel || !glow) {
      return;
    }
    const rect = panel.getBoundingClientRect();
    glow.style.translate = `${Math.round(event.clientX - rect.left - rect.width / 2)}px ${Math.round(
      event.clientY - rect.top - rect.height / 2,
    )}px`;
    glow.style.opacity = "1";
  }

  function handleLeave() {
    const glow = glowRef.current;
    if (glow) {
      glow.style.opacity = "0";
    }
  }

  return (
    <Link
      href={href}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      className="focus-visible:outline-marketing-blue group flex h-full flex-col rounded-[20px] bg-[#08080a] p-2.5 transition-shadow duration-300 ease-out hover:shadow-[0_18px_40px_rgba(0,0,0,0.20)] focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <span
        ref={panelRef}
        className="bg-marketing-blue relative block overflow-hidden rounded-[10px]"
      >
        <span className="block aspect-[204/150] transition-transform duration-700 ease-out group-hover:scale-[1.04]">
          {children}
        </span>
        <span
          ref={glowRef}
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-1/2 -mt-[110px] -ml-[110px] block size-[220px] rounded-full opacity-0 transition-opacity duration-300 will-change-transform"
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.12) 45%, rgba(255,255,255,0) 70%)",
            mixBlendMode: "soft-light",
          }}
        />
      </span>
      <h3
        className="convergence-pillar-title mt-7 px-1.5 pb-6 font-medium whitespace-pre-line text-white"
        style={{ letterSpacing: "-0.045em" }}
      >
        {title}
      </h3>
    </Link>
  );
}
